'use client';

import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type SVGProps,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  DocumentIcon,
  FolderIcon,
  LanguageIcon,
  LinkIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  QuestionMarkCircleIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SignalIcon,
  Squares2X2Icon,
  TableCellsIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import {
  ALL_CATEGORY,
  SHORTCUTS_CATEGORY,
  useCategoryLabel,
  useHelpData,
  useHelpText,
} from './useHelpData';
import type { HelpBadge, HelpFeature } from './helpData';
import { MODIFIER_KEYS, highlightTarget, normalizeCapturedKey } from './shortcutKeys';
import './HelpCenter.css';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

interface HelpCenterModalProps {
  /** Controlled visibility. When omitted the panel manages itself. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CATEGORY_ICONS: Record<string, IconType> = {
  Authentication: LockClosedIcon,
  'Drive - Files': DocumentIcon,
  'Drive - Folders': FolderIcon,
  'Drive - View': TableCellsIcon,
  Sharing: LinkIcon,
  Security: ShieldCheckIcon,
  Realtime: SignalIcon,
  Admin: ChartBarIcon,
  System: ServerStackIcon,
  Settings: LanguageIcon,
};

/** Literal-typed badge → class / translation-key maps so t() keys stay checked. */
const BADGE_CLASS: Record<HelpBadge, string> = {
  NEW: 'help-badge help-badge-new',
  UPDATED: 'help-badge help-badge-updated',
  BASELINE: 'help-badge help-badge-baseline',
  MODIFIED: 'help-badge help-badge-updated',
  DEPRECATED: 'help-badge help-badge-deprecated',
};

const BADGE_LABEL_KEY: Record<
  HelpBadge,
  | 'helpCenter.badges.NEW'
  | 'helpCenter.badges.UPDATED'
  | 'helpCenter.badges.BASELINE'
  | 'helpCenter.badges.MODIFIED'
  | 'helpCenter.badges.DEPRECATED'
> = {
  NEW: 'helpCenter.badges.NEW',
  UPDATED: 'helpCenter.badges.UPDATED',
  BASELINE: 'helpCenter.badges.BASELINE',
  MODIFIED: 'helpCenter.badges.MODIFIED',
  DEPRECATED: 'helpCenter.badges.DEPRECATED',
};

const IS_MAC =
  typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

const KEY_SYMBOLS: Record<string, string> = {
  Mod: IS_MAC ? '⌘' : 'Ctrl',
  Shift: '⇧',
  Alt: IS_MAC ? '⌥' : 'Alt',
  Enter: '⏎',
  Esc: '⎋',
  Tab: '⇥',
  Backspace: '⌫',
  Delete: 'Del',
  Space: 'Space',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

function keySymbol(token: string): string {
  return KEY_SYMBOLS[token] ?? token;
}

function Kbd({ combo }: { combo: string }) {
  return (
    <span className="inline-flex flex-shrink-0 items-center gap-1">
      {combo.split('+').map((token, index) => (
        <kbd key={`${token}-${index}`} className="help-kbd">
          {keySymbol(token)}
        </kbd>
      ))}
    </span>
  );
}

function BadgePill({ badge }: { badge: HelpBadge }) {
  const { t } = useTranslation();
  return <span className={BADGE_CLASS[badge]}>{t(BADGE_LABEL_KEY[badge])}</span>;
}

interface FeatureCardProps {
  feature: HelpFeature;
  onGo: (feature: HelpFeature) => void;
}

function FeatureCard({ feature, onGo }: FeatureCardProps) {
  const { t } = useTranslation();
  const entryText = useHelpText();
  const text = entryText(feature);
  const adminOnly = feature.roles.length > 0 && feature.roles.every((role) => role === 'admin');

  return (
    <article className="help-card rounded-xl border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="help-text truncate text-sm font-semibold">{text.name}</h3>
            <BadgePill badge={feature.badge} />
            {adminOnly && <span className="help-chip flex-shrink-0">{t('helpCenter.adminOnly')}</span>}
          </div>
          <p className="help-text-secondary mt-1 text-sm leading-relaxed">{text.description}</p>
        </div>

        {feature.route ? (
          <button
            type="button"
            onClick={() => onGo(feature)}
            className="help-btn-primary flex-shrink-0 whitespace-nowrap"
          >
            {t('helpCenter.open')}
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : (
          <span className="help-scope-chip flex-shrink-0 whitespace-nowrap">
            {feature.trigger.includes('auto')
              ? t('helpCenter.scopes.automatic')
              : feature.endpoint
                ? t('helpCenter.scopes.apiOnly')
                : t('helpCenter.scopes.uiAction')}
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <span className="help-meta-item" title={text.uiPath}>
          <MapPinIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span className="truncate">{text.uiPath}</span>
        </span>
        {feature.endpoint && (
          <code className="help-code max-w-full" title={feature.endpoint}>
            {feature.endpoint}
          </code>
        )}
        <span className="help-meta-item">
          {feature.shortcut ? (
            <Kbd combo={feature.shortcut} />
          ) : (
            <span className="help-text-tertiary">{t('helpCenter.noShortcut')}</span>
          )}
        </span>
      </div>
    </article>
  );
}

interface ShortcutMatrixProps {
  features: HelpFeature[];
  assignments: Record<string, string>;
  capturingId: string | null;
  onAssign: (featureId: string) => void;
  onClear: (featureId: string) => void;
}

function ShortcutMatrix({ features, assignments, capturingId, onAssign, onClear }: ShortcutMatrixProps) {
  const { t } = useTranslation();
  const entryText = useHelpText();
  const categoryLabel = useCategoryLabel();

  return (
    <div className="space-y-2">
      <p className="help-text-tertiary pb-1 text-xs leading-relaxed">
        {t('helpCenter.matrix.intro')}
      </p>

      {features.map((feature) => {
        const text = entryText(feature);
        const assigned = assignments[feature.id];
        const isCapturing = capturingId === feature.id;

        return (
          <div
            key={feature.id}
            className="help-matrix-row grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 rounded-lg border px-3.5 py-2.5 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_auto]"
          >
            <div className="min-w-0">
              <p className="help-text truncate text-sm font-medium">{text.name}</p>
              <p className="help-text-tertiary truncate text-xs">{categoryLabel(feature.category)}</p>
            </div>

            <p
              className="help-text-secondary hidden truncate text-xs md:block"
              title={text.uiPath}
            >
              {text.uiPath}
            </p>

            <div className="flex flex-nowrap items-center justify-end gap-2">
              {isCapturing ? (
                <span className="help-chip help-chip-capturing">{t('helpCenter.matrix.pressKeys')}</span>
              ) : assigned ? (
                <>
                  <Kbd combo={assigned} />
                  <button
                    type="button"
                    onClick={() => onClear(feature.id)}
                    className="help-icon-btn"
                    aria-label={t('helpCenter.matrix.clearAria', { name: text.name })}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </>
              ) : (
                <>
                  <span className="help-text-tertiary text-xs">{t('helpCenter.matrix.notSet')}</span>
                  <button
                    type="button"
                    onClick={() => onAssign(feature.id)}
                    className="help-btn-ghost flex-shrink-0 whitespace-nowrap"
                  >
                    {t('helpCenter.matrix.assign')}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function HelpCenterModal({ open: controlledOpen, onOpenChange }: HelpCenterModalProps) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (controlledOpen !== undefined) onOpenChange?.(next);
      else setInternalOpen(next);
    },
    [controlledOpen, onOpenChange],
  );

  const {
    query,
    setQuery,
    category,
    setCategory,
    categories,
    features,
    matrixFeatures,
    totalCount,
    clearSearch,
    categoryLabel,
  } = useHelpData();

  const [shortcutAssignments, setShortcutAssignments] = useLocalStorage<Record<string, string>>(
    'help-center.shortcut-overrides',
    {},
  );
  const [capturingId, setCapturingId] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Global hotkey: Cmd/Ctrl+K toggles the panel.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(!isOpen);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, setOpen]);

  // Focus the search field after opening; release capture mode on close.
  useEffect(() => {
    if (!isOpen) {
      setCapturingId(null);
      return;
    }
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  // Shortcut capture listener (capture phase: wins over the Dialog Escape handler).
  useEffect(() => {
    if (!capturingId) return;

    const onKeyDown = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Escape') {
        setCapturingId(null);
        return;
      }
      if (MODIFIER_KEYS.includes(event.key)) return;

      const parts: string[] = [];
      if (event.metaKey || event.ctrlKey) parts.push('Mod');
      if (event.shiftKey) parts.push('Shift');
      if (event.altKey) parts.push('Alt');
      parts.push(normalizeCapturedKey(event.key));

      setShortcutAssignments((prev) => ({ ...prev, [capturingId]: parts.join('+') }));
      setCapturingId(null);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [capturingId, setShortcutAssignments]);

  const handleGo = useCallback(
    (feature: HelpFeature) => {
      setOpen(false);
      if (feature.route) navigate(feature.route);
      if (feature.targetId) {
        const delay = feature.route ? 350 : 180;
        window.setTimeout(() => highlightTarget(feature.targetId as string), delay);
      }
    },
    [navigate, setOpen],
  );

  const handleClearShortcut = useCallback(
    (featureId: string) => {
      setShortcutAssignments((prev) => {
        const next = { ...prev };
        delete next[featureId];
        return next;
      });
    },
    [setShortcutAssignments],
  );

  const showMatrix = category === SHORTCUTS_CATEGORY;
  const visibleFeatures = showMatrix ? matrixFeatures : features;
  const viewTitle = showMatrix
    ? t('helpCenter.chrome.shortcutsTitle')
    : category === ALL_CATEGORY
      ? t('helpCenter.chrome.allFeatures')
      : categoryLabel(category);
  const resultLabel = query
    ? t('helpCenter.resultsCount', { count: visibleFeatures.length })
    : showMatrix
      ? t('helpCenter.entriesCount', { count: matrixFeatures.length })
      : t('helpCenter.resultsOf', { count: visibleFeatures.length, total: totalCount });
  const assignedShortcutCount = Object.keys(shortcutAssignments).length;

  const desktopCategories = categories.filter((cat) => cat.id !== SHORTCUTS_CATEGORY);
  const shortcutsEntry = categories.find((cat) => cat.id === SHORTCUTS_CATEGORY);

  const renderCategoryButton = (cat: { id: string; label: string; count: number }, compact: boolean) => {
    const Icon = CATEGORY_ICONS[cat.id] ?? Squares2X2Icon;
    const active = category === cat.id;
    return (
      <button
        key={cat.id}
        type="button"
        onClick={() => setCategory(cat.id)}
        aria-current={active ? 'true' : undefined}
        className={`help-nav-item flex flex-nowrap items-center ${
          compact
            ? 'flex-shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs'
            : 'w-full rounded-lg px-2.5 py-2 text-sm'
        } ${active ? 'help-nav-item-active' : ''}`}
      >
        <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        <span className={`min-w-0 ${compact ? '' : 'flex-1 truncate px-1 text-left'}`}>
          {cat.label}
        </span>
        <span className="help-count flex-shrink-0">{cat.id === SHORTCUTS_CATEGORY ? assignedShortcutCount : cat.count}</span>
      </button>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="help-backdrop fixed inset-0 backdrop-blur-xl" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4 sm:p-6">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-2 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-95"
          >
            <Dialog.Panel className="help-panel suite-ui-dialog flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden">
              {/* Search header */}
              <div className="help-header flex flex-shrink-0 flex-nowrap items-center gap-3 border-b px-4 py-3 sm:px-5">
                <MagnifyingGlassIcon className="help-icon h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('helpCenter.chrome.searchPlaceholder')}
                  className="help-search-input min-w-0 flex-1"
                  aria-label={t('helpCenter.chrome.searchAria')}
                />
                {query ? (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="help-icon-btn flex-shrink-0"
                    aria-label={t('helpCenter.chrome.clearSearch')}
                  >
                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                ) : (
                  <Kbd combo="Mod+K" />
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="help-icon-btn flex-shrink-0"
                  aria-label={t('helpCenter.chrome.closeAria')}
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              {/* Mobile category rail — stays inline, scrolls horizontally */}
              <div className="help-rail help-scroll flex flex-shrink-0 flex-nowrap gap-1.5 overflow-x-auto border-b px-3 py-2 md:hidden">
                {categories.map((cat) => renderCategoryButton(cat, true))}
              </div>

              <div className="flex min-h-0 flex-1">
                {/* Sidebar (desktop) */}
                <nav
                  className="help-sidebar hidden w-60 flex-shrink-0 flex-col border-r md:flex"
                  aria-label={t('helpCenter.chrome.categoriesAria')}
                >
                  <div className="help-scroll min-h-0 flex-1 overflow-y-auto p-2">
                    <p className="help-nav-section px-2 pb-1 pt-2">{t('helpCenter.chrome.browse')}</p>
                    <div className="space-y-0.5">
                      {desktopCategories.map((cat) => renderCategoryButton(cat, false))}
                    </div>
                  </div>

                  {shortcutsEntry && (
                    <div className="help-sidebar-divider border-t p-2">
                      {renderCategoryButton(shortcutsEntry, false)}
                    </div>
                  )}
                </nav>

                {/* Main column */}
                <section className="flex min-w-0 flex-1 flex-col">
                  <header className="flex flex-nowrap items-baseline justify-between gap-3 px-4 pb-2 pt-4 sm:px-5">
                    <Dialog.Title
                      as="h2"
                      className="help-text flex-shrink-0 whitespace-nowrap text-sm font-semibold"
                    >
                      {viewTitle}
                    </Dialog.Title>
                    <span className="help-text-tertiary flex-shrink-0 whitespace-nowrap text-xs">
                      {resultLabel}
                    </span>
                  </header>

                  <div className="help-scroll min-h-0 flex-1 overflow-y-auto px-4 pb-5 sm:px-5">
                    {showMatrix ? (
                      <ShortcutMatrix
                        features={matrixFeatures}
                        assignments={shortcutAssignments}
                        capturingId={capturingId}
                        onAssign={setCapturingId}
                        onClear={handleClearShortcut}
                      />
                    ) : visibleFeatures.length > 0 ? (
                      <div className="space-y-2.5">
                        {visibleFeatures.map((feature) => (
                          <FeatureCard key={feature.id} feature={feature} onGo={handleGo} />
                        ))}
                      </div>
                    ) : (
                      <div className="flex min-h-[240px] flex-col items-center justify-center gap-2 px-6 text-center">
                        <QuestionMarkCircleIcon className="help-icon h-10 w-10" aria-hidden="true" />
                        <p className="help-text text-sm font-medium">{t('helpCenter.empty.noMatches', { query })}</p>
                        <p className="help-text-tertiary text-xs">
                          {t('helpCenter.empty.hint')}
                        </p>
                        <button type="button" onClick={clearSearch} className="help-btn-ghost mt-1">
                          {t('helpCenter.empty.clear')}
                        </button>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
